//MainLayout.js
import { createRef, useRef } from "react";
import { Outlet, Link, useMatches, useLocation } from "react-router-dom";
// transition provides component effects (page sliding, menu sliding, etc)
import { CSSTransition, SwitchTransition } from "react-transition-group";

// AppShell standard to wrap header, main, and footer
import AppShell from "./AppShell";

// layout provider context provides header, footer injection as well as display option
import { useLayout } from "../providers/LayoutProvider";

//header footer components are standard for all layouts
import { Header } from "./Header";
import { Footer } from "./Footer";

// defined primitives
import { Brand, Copyright, Menu } from "./primitives";

/**
 * App shell layout: header, main content, footer, scroll restore.
 */
export default function MainLayout() {
  const { headerContent, footerContent, showHeader, showFooter } = useLayout();
  const location = useLocation();
  const matches = useMatches();

  // used in page transitions to pass nodeRef created in routes to page component
  // const { handle } =
  //   matches.find((match) => match.pathname === location.pathname) ?? {};
  // const { nodeRef } = handle ?? {};
  // const pageClassName = location.pathname.replaceAll("/", "") ?? "";
  //  className={`page ${pageClassName || ""}`}
  // const { theme } = useTheme();

  // Map of pathname -> React.RefObject<Element>

  /**
   * Initializes pageRefs.current to a new JavaScript Map object during the initial render.
   *   On subsequent re-renders, the same Map object is returned, preserving its contents across renders.
   * This Map will be used to store a collection of individual refs, keyed by their pathnames.
   */
  const pageRefs = useRef(new Map());

  /**
   * This function acts as a memoization layer. It ensures that for a given path,
   * you always get the same reference.
   *
   * If no entry exists, pageRefs.current.set(path, createRef()); is called.
   * It creates a new ref using createRef() and stores it in the Map with the path as the key.
   * If an entry already exists (or after a new one is created),
   * the function returns the existing ref with pageRefs.current.get(path).
   *
   * @param {*} path
   * @returns
   */
  const getStableRefForPath = (path) => {
    if (!pageRefs.current.has(path)) {
      pageRefs.current.set(path, createRef());
    }
    return pageRefs.current.get(path);
  };

  // finds the matching route configuration for the current location.pathname from a list of matches.
  // This pattern is common in routing libraries like React Router.
  const match = matches.find((m) => m.pathname === location.pathname);

  // checks if the matching route already provides a nodeRef via its handle property.
  const routeHandleRef = match?.handle?.nodeRef;

  /**
   * Sets the final nodeRef variable. It uses the ?? (nullish coalescing) operator.
   * It will first try to use the routeHandleRef provided by the router, if it exists.
   * If routeHandleRef is null or undefined, it falls back to calling getStableRefForPath
   *  to either get an existing stable ref from the Map or create a new one for the current path.
   */
  const nodeRef = routeHandleRef ?? getStableRefForPath(location.pathname);

  const header = (
    <Header
      className="app-header"
      size="lg"
      start={
        <Link key="/" to="/" className="app-name" aria-label="PWA Client">
          <Brand>PWA Client</Brand>
        </Link>
      }
      end={<Menu />} //{<UserMenu />}
      sticky
    />
  );

  const footer = (
    <Footer
      className="app-footer fixed-bottom"
      size="md"
      center={
        <div className="w-100 d-flex flex-column align-items-center">
          <span className="opacity-70">© {new Date().getFullYear()}</span>
        </div>
      }
    />
  );

  // id must equal "layout"
  const main = (
    <main id="layout" role="main" className="layout flex-1">
      <div key={location.pathname} nodeRef={nodeRef} className="page">
        <Outlet />
      </div>
    </main>
  );

  return (
    <AppShell className="main" dataLayout="main">
      {/* <Header /> */}
      {/* {headerContent || <Header />} */}
      {showHeader ? headerContent || header : null}
      {main}
      {showFooter ? footerContent || footer : null}
      {/* {footerContent || <Footer />} */}
      {/* <Footer /> */}
      {/* Back/forward restore + reset on new pages */}
      {/* <ScrollRestoration getKey={(loc) => loc.pathname + loc.hash} /> */}
    </AppShell>
  );
}
